import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/authcontext';
import api from '../../utils/axiosconfig';
import './Checkout.css';

const loadRazorpay = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const CheckoutSection = () => {
  const { user, bookingInfo, saveBookingInfo } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    email: '',
    phone: '',
    notes: '',
    needsCab: false
  });
  const [errors, setErrors] = useState({});
  const [showBookingError, setShowBookingError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData(prevState => ({
        ...prevState,
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        phone: user.contact_number || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    if (hasInteracted && (!bookingInfo || !isValidBooking(bookingInfo))) {
      setShowBookingError(true);
    } else {
      setShowBookingError(false);
    }
  }, [bookingInfo, hasInteracted]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: null }));
    }
    setHasInteracted(true);
  };

  const isValidBooking = (booking) => {
    if (!booking) return false;
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const fromDate = new Date(booking.from_date);
    return booking.number_of_rooms > 0 && fromDate >= currentDate;
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      if (!formData[key] && key !== 'notes' && key !== 'needsCab') {
        newErrors[key] = 'This field is required';
      }
    });

    if (!bookingInfo || !isValidBooking(bookingInfo)) {
      newErrors.booking = 'Invalid booking information. Please go back and update your booking.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const initializeRazorpayPayment = async (bookingId) => {
    const res = await loadRazorpay();

    if (!res) {
      alert('Razorpay SDK failed to load');
      return;
    }

    try {
      setPaymentProcessing(true);
      const orderResponse = await api.post('/payment/create-order', {
        booking_id: bookingId
      });

      const { data } = orderResponse.data;
      const RbookingId = `SRIHS${bookingId.toString().padStart(4, '0')}`;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        name: "Shri Ram International Room Stay",
        description: RbookingId,
        order_id: data.orderId,
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: formData.phone
        },
        handler: async function (response) {
          try {
            const verifyResponse = await api.post('/payment/verify', {
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature
            });
            console.log(response);
            // Payment successful
            if (verifyResponse.data.status) {
              localStorage.removeItem('bookingInfo')
              saveBookingInfo(null);
              navigate(`/confirmation/${bookingId}`);
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            console.error('Error processing payment:', error);
            setErrors({ api: 'Payment processing failed. Please try again.' });
          }
        },
        modal: {
          ondismiss: function () {
            setPaymentProcessing(false);
          }
        },
        theme: {
          color: "#3399cc"
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error initializing payment:', error);
      setErrors({ api: 'Failed to initialize payment. Please try again.' });
      setPaymentProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasInteracted(true);
    if (validateForm()) {
      setIsLoading(true);
      try {
        const bookingResponse = await api.post('/booking', {
          special_comments: formData.notes,
          from_date: bookingInfo.from_date,
          to_date: bookingInfo.to_date,
          room_type: "AC",
          number_of_rooms: bookingInfo.number_of_rooms,
          needs_cab: formData.needsCab
        });

        if (bookingResponse.data.data && bookingResponse.data.data.id) {
          await initializeRazorpayPayment(bookingResponse.data.data.id);
        } else {
          throw new Error('Booking failed');
        }
      } catch (error) {
        console.error('Error making booking:', error);
        setErrors({ api: 'Failed to make booking. Please try again.' });
        window.scrollTo(0, 0);
      } finally {
        setIsLoading(false);
      }
    } else {
      window.scrollTo(0, 0);
    }
  };

  const calculateNights = () => {
    if (bookingInfo && bookingInfo.from_date && bookingInfo.to_date) {
      const start = new Date(bookingInfo.from_date);
      const end = new Date(bookingInfo.to_date);
      return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    }
    return 0;
  };

  const nights = calculateNights();
  const basePrice = bookingInfo ? bookingInfo.total_price : 0;
  const taxRate = 0.12;
  const taxAmount = basePrice * taxRate;
  const totalAmount = basePrice;

  return (
    <>
      <section className="section-sub-banner bg-9">
        <div className="awe-overlay"></div>
        <div className="sub-banner">
          <div className="container">
            <div className="text text-center">
              <h2>CheckOut</h2>
            </div>
          </div>
        </div>
      </section>
      <section className="section-checkout">
        <Container>
          {showBookingError && (
            <Alert variant="danger" onClose={() => setShowBookingError(false)} dismissible>
              Invalid booking information. Please go back and update your booking.
            </Alert>
          )}
          {errors.api && (
            <Alert variant="danger">
              {errors.api}
            </Alert>
          )}
          <div className="checkout">
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <div className="checkout_head">
                    <h3>BILLING DETAILS</h3>
                  </div>
                  <div className="checkout_form">
                    <Row>
                      <Col xs={6}>
                        <Form.Group>
                          <Form.Label>First Name*</Form.Label>
                          <Form.Control
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            isInvalid={!!errors.firstName}
                          />
                          {errors.firstName &&
                            <Form.Control.Feedback type="invalid">
                              {errors.firstName}
                            </Form.Control.Feedback>
                          }
                        </Form.Group>
                      </Col>
                      <Col xs={6}>
                        <Form.Group>
                          <Form.Label>Last Name*</Form.Label>
                          <Form.Control
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            isInvalid={!!errors.lastName}
                          />
                          {errors.lastName &&
                            <Form.Control.Feedback type="invalid">
                              {errors.lastName}
                            </Form.Control.Feedback>
                          }
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Form.Group style={{ marginTop: '20px' }}>
                        <Form.Label>Address* </Form.Label>
                        <Form.Control
                          as="textarea"
                          placeholder="Address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          isInvalid={!!errors.address}
                        />
                        {errors.address &&
                          <Form.Control.Feedback type="invalid">
                            {errors.address}
                          </Form.Control.Feedback>
                        }
                      </Form.Group>
                    </Row>
                    <Row>
                      <Col xs={6}>
                        <Form.Group>
                          <Form.Label>Email Address*</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            isInvalid={!!errors.email}
                          />
                          {errors.email &&
                            <Form.Control.Feedback type="invalid">
                              {errors.email}
                            </Form.Control.Feedback>
                          }
                        </Form.Group>
                      </Col>
                      <Col xs={6}>
                        <Form.Group>
                          <Form.Label>Phone*</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            isInvalid={!!errors.phone}
                          />
                          {errors.phone &&
                            <Form.Control.Feedback type="invalid">
                              {errors.phone}
                            </Form.Control.Feedback>
                          }
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group style={{ marginTop: '20px' }}>
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Notes about your order, eg. special notes for delivery"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                    <Form.Group className="checkbox-group">
                    <Form.Check
                      type="checkbox"
                      id="needsCab"
                      name="needsCab"
                      label="Do you need cab service?"
                      checked={formData.needsCab}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="checkout_head">
                    <h3>YOUR PAYMENT DETAILS</h3>
                  </div>
                  <Card className="checkout_cart price-card">
                    <Card.Body>
                      <h5 className="mb-3 price-title"><strong>Price Breakup</strong></h5>
                      <div className="d-flex justify-content-between mb-2 price-item">
                        <span>{bookingInfo ? bookingInfo.number_of_rooms : 0} Room(s) x {nights} Night(s)</span>
                        <span className="price-amount">₹ {basePrice.toLocaleString()}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2 price-item">
                        <span>Base Price</span>
                        <span className="price-amount color-red">₹ {basePrice.toLocaleString()}</span>
                      </div>
                      <hr />
                      <div className="d-flex justify-content-between price-total">
                        <strong>Total Amount to be paid</strong>
                        <strong className="price-amount">₹ {totalAmount.toLocaleString()}</strong>
                      </div>
                    </Card.Body>
                  </Card>
                  <div className="checkout_option mt-4">
                    <div className="payment-method-info">
                      <h5>Payment Method</h5>
                      <p>Secure payment via Razorpay - Pay using Credit/Debit Card, UPI, Net Banking, or Wallet</p>
                    </div>
                  </div>
                  <div className="checkout_btn">
                    <Button
                      type="submit"
                      className="submit-btn"
                      style={{ width: '100%' }}
                      disabled={isLoading || paymentProcessing}
                    >
                      {isLoading ? 'Processing...' : paymentProcessing ? 'Initializing Payment...' : 'Pay Now ₹' + totalAmount.toLocaleString()}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </div>
        </Container>
      </section>
    </>
  );
};

export default CheckoutSection;